import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { billingApi, type BookingCostFilters, type BookingCostRow } from "@/lib/api/billing";
import { monthParam, resetFiltersForMonth } from "@/lib/booking-costs";
import { downloadInvoicePdf, saveBlob } from "@/lib/invoices";
import { BookingCostsView } from "./booking-costs-view";

const status = (error: unknown): number | true | null =>
  error ? ((error as { response?: { status?: number } }).response?.status ?? true) : null;

/** Invoices → Booking costs (tenant admins). */
export function CompanyBookingCosts() {
  const [month, setMonth] = useState(monthParam());
  const [filters, setFilters] = useState<Omit<BookingCostFilters, "month">>({ member: null, room: null, reason: null });
  // The member/room options are built from the selected month's rows only — carrying the old month's
  // selection forward can match nothing in the new one (an empty placeholder, "No charged bookings").
  const onMonthChange = (next: string) => {
    setMonth(next);
    setFilters(resetFiltersForMonth);
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ["booking-costs", month, filters],
    queryFn: () => billingApi.getBookingCosts({ month, ...filters }).then((r) => r.data),
    placeholderData: keepPreviousData,
    retry: (count, err) => (err as { response?: { status?: number } })?.response?.status !== 403 && count < 2,
  });
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  // Spec §8: the Invoice column opens the invoice's PDF — the same download as the Invoices tab.
  const onInvoicePdf = async (invoice: NonNullable<BookingCostRow["invoice"]>) => {
    if (downloadingId !== null) return; // one at a time: each fetch counts against the rate limit
    setDownloadingId(invoice.id);
    const message = await downloadInvoicePdf(invoice, { fetchPdf: billingApi.getInvoicePdf, save: saveBlob });
    setDownloadingId(null);
    if (message) toast.error(message);
  };
  return (
    <BookingCostsView
      company
      data={data}
      isLoading={isLoading}
      error={status(error)}
      month={month}
      onMonthChange={onMonthChange}
      filters={filters}
      onFiltersChange={setFilters}
      onInvoicePdf={onInvoicePdf}
      downloadingInvoiceId={downloadingId}
    />
  );
}

/** My bookings → Past: the member's own charged bookings. */
export function MyBookingCosts() {
  const [month, setMonth] = useState(monthParam());
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-booking-costs", month],
    queryFn: () => billingApi.getMyBookingCosts(month).then((r) => r.data),
    placeholderData: keepPreviousData,
  });
  return (
    <BookingCostsView company={false} data={data} isLoading={isLoading} error={status(error)} month={month} onMonthChange={setMonth} />
  );
}
